import { Link } from 'react-router-dom';
import { Briefcase, PushPin } from '@phosphor-icons/react';
import { useQuickAccessMatters } from '@/lib/matterQuickAccessStore';

/**
 * "Jump back in" — multi-matter context keeper. A barrister juggles several matters a day
 * (A → urgent email → B → court prep → C → fee note → back to A). Recent matters are already
 * tracked on open (matterQuickAccessStore.trackAccess); this surfaces them — pinned first, then
 * recently-opened — so switching back is one tap, not a re-search. Renders nothing when empty.
 */
export function MatterQuickSwitch({ basePath = '/barrister' }: { basePath?: string }) {
  const { pinned, recent, hasQuickAccess } = useQuickAccessMatters();
  if (!hasQuickAccess) return null;

  const items = [
    ...pinned.map((m) => ({ ...m, isPinned: true })),
    ...recent.map((m) => ({ ...m, isPinned: false })),
  ];

  return (
    <div className="rounded-xl border border-border/60 bg-card p-3">
      <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        Jump back in
      </div>
      <div className="flex flex-wrap gap-2">
        {items.map((m) => (
          <Link
            key={m.id}
            to={`${basePath}/matters/${m.id}`}
            className="group flex items-center gap-2 rounded-lg border border-border/60 bg-background px-3 py-2 transition hover:border-brand-300 hover:bg-brand-50/40"
          >
            {m.isPinned ? (
              <PushPin className="h-3.5 w-3.5 shrink-0 text-brand-600 dark:text-brand-400" weight="fill" />
            ) : (
              <Briefcase className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            )}
            <span className="max-w-[180px] truncate text-sm font-medium text-foreground">{m.name}</span>
            {m.number && <span className="shrink-0 text-xs text-muted-foreground">{m.number}</span>}
          </Link>
        ))}
      </div>
    </div>
  );
}
